import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useTheme,
  alpha,
  Stack,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Article as ArticleIcon,
  Logout as LogoutIcon,
  People as PeopleIcon,
  Newspaper as NewsIcon,
  Notifications as NotificationsIcon,
  AdminPanelSettings as AdminIcon,
  School as AlumniIcon,
  Public as PublicIcon,
  SmartToy as BotIcon,
  Mail as MailIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { SettingsProvider, useSettings } from '../../../contexts/SettingsContext';
import LanguageSwitcher from '../../../components/LanguageSwitcher';
import AuthenticatedAvatar from '../../../components/AuthenticatedAvatar';
import { usePermissions } from '../../../hooks/usePermissions';
import { routeResourceMap, hasRoleAccess } from '../../../config/routeResourceMap';

const drawerWidth = 260;

function AdminLayoutContent() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { canAccess } = usePermissions();
  const { settings } = useSettings();

  // Update page title when settings load
  useEffect(() => {
    if (settings?.site_title) {
      document.title = `${settings.site_title} - Admin Portal`;
    }
  }, [settings?.site_title]);

  // Update favicon when settings load
  useEffect(() => {
    if (settings?.favicon_url) {
      const link: HTMLLinkElement = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      const faviconUrl = settings.favicon_url.startsWith('http') 
        ? settings.favicon_url 
        : `https://cdn-aims.amy.care${settings.favicon_url.replace('/cdn', '')}`;
      link.href = faviconUrl;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }, [settings?.favicon_url]);

  const allMenuItems = [
    { text: t('nav.dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
    { text: t('nav.alumni'), icon: <PeopleIcon />, path: '/alumni' },
    { text: t('nav.news'), icon: <NewsIcon />, path: '/news' },
    { text: t('nav.adminUsers'), icon: <AdminIcon />, path: '/admin-users' },
    { text: t('nav.alumniUsers'), icon: <AlumniIcon />, path: '/alumni-users' },
    { text: t('nav.masterData'), icon: <PublicIcon />, path: '/master-data' },
    { text: 'LINE Bot', icon: <BotIcon />, path: '/line-bot' },
    { text: 'จัดการข้อความ', icon: <MailIcon />, path: '/messages' },
    { text: 'Legal Documents', icon: <ArticleIcon />, path: '/legal-documents' },
    { text: t('nav.siteSettings'), icon: <SettingsIcon />, path: '/settings' },
    { text: t('nav.rbacManagement'), icon: <SecurityIcon />, path: '/rbac' },
    { text: t('nav.activityLogs'), icon: <ArticleIcon />, path: '/logs' },
  ];

  // Filter menu items based on database permissions with role-based fallback
  const menuItems = allMenuItems.filter(item => {
    const routeMapping = routeResourceMap[item.path];
    
    if (routeMapping) {
      // Check database permissions first
      if (canAccess(routeMapping.resource, routeMapping.action)) {
        return true;
      }
    }
    
    // Fallback to role-based access (for super_admin and backward compatibility)
    return hasRoleAccess(user?.role, item.path);
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const theme = useTheme();

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        {settings?.logo_url ? (
          <Box
            component="img"
            src={
              settings.logo_url.startsWith('http') 
                ? settings.logo_url 
                : `https://cdn-aims.amy.care${settings.logo_url.replace('/cdn', '')}`
            }
            alt={settings.site_title || 'Logo'}
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 700,
              fontSize: '1.25rem',
            }}
          >
            {settings?.site_title?.[0] || 'A'}
          </Box>
        )}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {settings?.site_title || 'AIMS'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Admin Portal
          </Typography>
        </Box>
      </Box>
      <Divider />
      <List sx={{ px: 2, py: 2 }}>
        {menuItems.map((item) => {
          const isSelected = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isSelected}
                onClick={() => handleMenuClick(item.path)}
                sx={{
                  borderRadius: '8px',
                  mb: 0.5,
                  '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.main',
                    },
                  },
                  '&:hover': {
                    bgcolor: alpha(theme.palette.grey[500], 0.04),
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: isSelected ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: isSelected ? 600 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[1],
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 70 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { sm: 'none' },
              '&:hover': {
                bgcolor: alpha(theme.palette.grey[500], 0.08),
              },
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Stack direction="row" spacing={1} alignItems="center">
            <LanguageSwitcher />
            
            <IconButton
              size="large"
              sx={{
                '&:hover': {
                  bgcolor: alpha(theme.palette.grey[500], 0.08),
                },
              }}
            >
              <NotificationsIcon />
            </IconButton>
            
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                pl: 1,
                pr: 1,
                py: 0.5,
                borderRadius: '12px',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: alpha(theme.palette.grey[500], 0.08),
                },
              }}
              onClick={handleProfileMenuOpen}
            >
              <Stack direction="column" spacing={0} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
                  {user?.first_name} {user?.last_name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {user?.role}
                </Typography>
              </Stack>
              <AuthenticatedAvatar
                src={user?.avatar_url}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'primary.main',
                  fontWeight: 600,
                }}
              >
                {user?.first_name?.[0]}
              </AuthenticatedAvatar>
            </Box>
          </Stack>
          
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                borderRadius: '12px',
                boxShadow: theme.shadows[8],
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2">{user?.first_name} {user?.last_name}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {user?.email}
              </Typography>
              <Chip
                label={user?.role}
                size="small"
                color="primary"
                sx={{ mt: 1, height: 20, fontSize: '0.65rem' }}
              />
            </Box>
            <Divider />
            <MenuItem onClick={() => { navigate('/profile'); handleProfileMenuClose(); }} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <AuthenticatedAvatar
                  src={user?.avatar_url}
                  sx={{ width: 24, height: 24 }}
                >
                  {user?.first_name?.[0]}
                </AuthenticatedAvatar>
              </ListItemIcon>
              <Typography variant="body2">{t('common.profile')}</Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2">{t('common.logout')}</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'background.paper',
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'background.paper',
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
          mt: { xs: '64px', sm: '70px' },
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

function AdminLayout() {
  return (
    <SettingsProvider>
      <AdminLayoutContent />
    </SettingsProvider>
  );
}

export default AdminLayout;
